<script lang="ts" setup>
import type { Person } from "~~/sanity.types";

const props = defineProps<{
  person: Person;
}>();

const STATS_QUERY = `{
  "nuggets": count(*[_type == "nugget" && references($id)]),
  "posts":   count(*[_type == "post"   && references($id)]),
  "books":   count(*[_type == "book"   && references($id)]),
  "songs":   count(*[_type == "music"  && references($id)])
}`;

type PersonStats = {
  nuggets: number;
  posts: number;
  books: number;
  songs: number;
};

const { data: stats, pending } = useSanityQuery(
  STATS_QUERY as any,
  { id: props.person._id },
  {
    server: false,
    lazy: true,
  }
);

const pluralize = (word: string, count: number) =>
  count === 1 ? word : `${word}s`;

const displayStats = computed(() => {
  if (!stats.value) return [];
  const s = stats.value as PersonStats;
  const items = [];

  if (s.nuggets > 0)
    items.push(`${s.nuggets} ${pluralize("Nugget", s.nuggets)}`);
  if (s.posts > 0) items.push(`${s.posts} ${pluralize("Post", s.posts)}`);
  if (s.books > 0) items.push(`${s.books} ${pluralize("Book", s.books)}`);
  if (s.songs > 0) items.push(`${s.songs} ${pluralize("Song", s.songs)}`);

  return items;
});
</script>

<template>
  <div class="text-xs flex items-center gap-1 min-h-[1lh]">
    <template v-if="pending || !stats">
      <USkeleton class="h-3 w-40" />
    </template>
    <template v-else-if="displayStats.length > 0">
      <template v-for="(stat, index) in displayStats" :key="stat">
        <span class="text-gray-700 dark:text-gray-300">{{ stat }}</span>
        <span
          v-if="index < displayStats.length - 1"
          class="text-gray-500 dark:text-gray-500"
          >•</span
        >
      </template>
    </template>
  </div>
</template>
